import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useTitle } from 'react-use';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import { useReportEmptyState } from '@/hooks/useReportEmptyState';
import useLanguageModels from '@/modules/developerTools/hooks/useLanguageModels';
import { getPermissions } from '@/redux/permissions/selectors';
import { setSidebarContent, setSidebarMeta } from '@/redux/sidebar/actions';
import { selectSidebar } from '@/redux/sidebar/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { LANGUAGE_MODELS_DOCS_URL } from '@/util/constants';

import { DeleteLanguageModelModal } from './DeleteLanguageModelModal';
import { EmptyLanguageModels } from './EmptyLanguageModels';
import { providerNameMapper } from './helper';
import { LanguageModelsTable } from './LanguageModelsTable';
import { DEV_TOOLS_SIDEBAR_KEYS, LanguageModel } from '../../types';

interface LanguageModelsProps {
  onEmptyStateChange: (isEmpty: boolean) => void;
}

export const LanguageModels = ({ onEmptyStateChange }: LanguageModelsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const sidebarLanguageModelId = useSelector(
    (state: RootState) => selectSidebar(state).meta?.languageModelId
  );
  const { languageModels, listStatus, updateLanguageModel } = useLanguageModels();
  const [search, setSearch] = useState('');
  const [deleteModelId, setDeleteModelId] = useState<string | null>(null);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'language_models', actions.WRITE)
  );
  const canDelete = useSelector((state: RootState) =>
    getPermissions(state, 'language_models', actions.DELETE)
  );

  useTitle(t('pages.devtool_llms'));

  useSegments(PageName.LANGUAGE_MODELS);

  const handleAdd = useCallback(() => {
    dispatch(setSidebarContent(DEV_TOOLS_SIDEBAR_KEYS.models));
  }, [dispatch]);

  const handleEdit = useCallback(
    (model: LanguageModel) => {
      dispatch(setSidebarContent(DEV_TOOLS_SIDEBAR_KEYS.models));
      dispatch(
        setSidebarMeta({ languageModelId: model.language_model_id, provider: model.provider })
      );
    },
    [dispatch]
  );

  const handleDelete = useCallback((model: LanguageModel) => {
    setDeleteModelId(model.language_model_id);
  }, []);

  const handleDeleteClose = useCallback(() => {
    setDeleteModelId(null);
  }, []);

  const handleDeleted = useCallback(
    (id: string) => {
      if (id === sidebarLanguageModelId) {
        dispatch(setSidebarContent(null));
      }
    },
    [sidebarLanguageModelId, dispatch]
  );

  const handleToggleEnabled = useCallback(
    (model: LanguageModel) => {
      updateLanguageModel({ language_model_id: model.language_model_id, enabled: !model.enabled });
    },
    [updateLanguageModel]
  );

  const filteredModels = useMemo(() => {
    if (!search.trim()) {
      return languageModels;
    }
    const query = search.toLowerCase();
    return languageModels.filter(
      (model) =>
        model.model.toLowerCase().includes(query) ||
        providerNameMapper(model.provider).toLowerCase().includes(query)
    );
  }, [languageModels, search]);

  const isLoading = listStatus === 'pending';
  const { showEmpty, showEmptySkeleton, showList, isEmptyPage } = useEmptyStateView({
    scope: 'language-models',
    isLoading,
    isEmpty: !languageModels?.length,
    isError: listStatus === 'error',
  });

  useReportEmptyState(isEmptyPage, onEmptyStateChange);

  return (
    <>
      <ContextualHelp
        title={t('developer_tools.contextual_help.language_models_title')}
        name="about_language_models"
        links={[{ label: t('common.learn_more'), url: LANGUAGE_MODELS_DOCS_URL }]}
      >
        {t('developer_tools.contextual_help.language_models_message')}
      </ContextualHelp>

      {showEmpty && <EmptyLanguageModels onAdd={handleAdd} canWrite={canWrite} />}

      {showEmptySkeleton && <EmptySkeleton descriptionLines={2} />}

      {showList && (
        <>
          <div className="mb-4 pl-1">
            <SearchBar
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('developer_tools.language_models_search')}
            />
          </div>

          <LanguageModelsTable
            languageModels={filteredModels}
            isLoading={isLoading}
            canWrite={canWrite}
            canDelete={canDelete}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleEnabled={handleToggleEnabled}
            activeRowId={sidebarLanguageModelId || undefined}
          />
        </>
      )}
      {deleteModelId && (
        <DeleteLanguageModelModal
          languageModelId={deleteModelId}
          onClose={handleDeleteClose}
          onDeleted={handleDeleted}
        />
      )}
    </>
  );
};
